import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import BackendService from '../../services/admin/BackendService';
import { sanitizeEmailHTML } from '../../services/sanitizeHTML';
import { EmailPreviewIframeRef } from './EmailPreviewIframe';
import {
  InvitationEmailEditHintPosition,
  InvitationEmailTemplateData,
  InvitationEmailTemplateParts,
  InvitationEmailToastMessage,
} from './generateInvitationEmailTypes';
import {
  computeEditHintPosition,
  splitInvitationTemplateHtml,
  stripCustomMessagePlaceholder,
} from './generateInvitationEmailUtils';

export function useGenerateInvitationEmail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [templateData, setTemplateData] = useState<InvitationEmailTemplateData | null>(null);
  const [customHtml, setCustomHtml] = useState('');
  const [subject, setSubject] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [message, setMessage] = useState<InvitationEmailToastMessage | null>(null);
  const [templateParts, setTemplateParts] = useState<InvitationEmailTemplateParts | null>(null);
  const emailPreviewRef = useRef<EmailPreviewIframeRef>(null);
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  const [showSpeechBubble, setShowSpeechBubble] = useState(false);
  const [hasClickedEditable, setHasClickedEditable] = useState(false);
  const [elementPosition, setElementPosition] = useState<InvitationEmailEditHintPosition | null>(
    null,
  );

  const loadEmailTemplate = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [data, currentUser] = await Promise.all([
        new BackendService().getInvitationEmailTemplate(id),
        new BackendService().getCurrentUser(),
      ]);
      setTemplateData(data);
      setSubject(data.subject);
      setFromEmail(currentUser.email || '');
      setTemplateParts(splitInvitationTemplateHtml(data.html));
      setCustomHtml('');
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.error || 'Failed to load email template',
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      void loadEmailTemplate();
    }

    return () => {
      setShowSpeechBubble(false);
      setElementPosition(null);
      setHasClickedEditable(false);
    };
  }, [id, loadEmailTemplate]);

  const templateHtml = templateData ? stripCustomMessagePlaceholder(templateData.html) : '';

  const handleEnterEditMode = useCallback(() => {
    setHasClickedEditable((prev) => (prev ? prev : true));
    setShowSpeechBubble(false);
  }, []);

  const handleContentChange = useCallback((html: string) => {
    const sanitized = sanitizeEmailHTML(html, { allowAnchors: true });
    setCustomHtml(sanitized);
  }, []);

  const handleElementPosition = useCallback(
    (position: { top: number; left: number }) => {
      const containerEl = iframeContainerRef.current;
      if (!containerEl) {
        return;
      }

      const normalizedPosition = computeEditHintPosition(containerEl, position);
      if (normalizedPosition) {
        setElementPosition(normalizedPosition);
        if (!hasClickedEditable) {
          setShowSpeechBubble(true);
        }
      } else {
        setElementPosition(null);
        setShowSpeechBubble(false);
      }
    },
    [hasClickedEditable],
  );

  const handleHideSpeechBubble = useCallback(() => {
    if (!hasClickedEditable) {
      setHasClickedEditable(true);
      setShowSpeechBubble(false);
    }
  }, [hasClickedEditable]);

  useEffect(() => {
    if (elementPosition && !hasClickedEditable) {
      const timer = setTimeout(() => {
        setShowSpeechBubble(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [elementPosition, hasClickedEditable]);

  useEffect(() => {
    const isOnEmailPage = location.pathname.includes('/generateInvitationEmail');
    if (!isOnEmailPage) {
      setShowSpeechBubble(false);
      setElementPosition(null);
      setHasClickedEditable(false);
    }
  }, [location.pathname]);

  const handleSend = useCallback(async () => {
    if (!id) return;

    setSending(true);
    try {
      let customHtmlToSend = customHtml || '';
      if (emailPreviewRef.current) {
        const currentHtml = emailPreviewRef.current.getCurrentHtml();
        if (currentHtml) {
          customHtmlToSend = currentHtml;
        }
      }

      const sanitizedHtml = sanitizeEmailHTML(customHtmlToSend, {
        allowAnchors: true,
      });

      await new BackendService().sendInvitationEmail(id, {
        customHtml: sanitizedHtml,
        subject: subject || undefined,
        sendAsMe: true,
        reset: false,
      });

      setMessage({
        type: 'success',
        text: 'Email sent successfully',
      });

      setTimeout(() => {
        navigate(`/contacts/${id}`);
      }, 2000);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.error || 'Failed to send email',
      });
    } finally {
      setSending(false);
    }
  }, [customHtml, id, navigate, subject]);

  const navigateToContact = useCallback(() => {
    if (id) {
      navigate(`/contacts/${id}`);
    }
  }, [id, navigate]);

  const dismissMessage = useCallback(() => {
    setMessage(null);
  }, []);

  return {
    id,
    loading,
    sending,
    templateData,
    templateParts,
    templateHtml,
    customHtml,
    subject,
    setSubject,
    fromEmail,
    setFromEmail,
    message,
    emailPreviewRef,
    iframeContainerRef,
    showSpeechBubble,
    elementPosition,
    handleEnterEditMode,
    handleContentChange,
    handleElementPosition,
    handleHideSpeechBubble,
    handleSend,
    navigateToContact,
    dismissMessage,
  };
}
